import { useMemo } from 'react';

import { IClientAllocationGroup } from '~/services/api/clientAllocation/types';
import { IDateRange, IParams } from '~/services/api/types';
import { IClientAllocationFilters, IClientAllocationStore } from '~/store/clientAllocation/types';
import { useStoreActions, useStoreState } from '~/store/hooks';
import useRole from '~/store/user/hooks/useRole';
import { getLocalDateNoFormat } from '~/utils/date/date';
import AllocationTypes from '../../reusable/ClientAllocationForm/constants/allocationTypes';

interface IUseGetAllocationsReturn {
  getAllocations: (payload: {
    clinicId: string;
    teamId: string;
    params?: IParams & IDateRange;
  }) => Promise<void>;
  list: IClientAllocationGroup[];
  dateRange: Date[];
  currentFilters: {
    filters: IClientAllocationFilters;
    pagination: IClientAllocationStore['pagination'];
  };
}

const useGetAllocations = (): IUseGetAllocationsReturn => {
  const { isGlobalUser } = useRole();

  const { onGetClientAllocations, onGetGlobalUserClientAllocations } = useStoreActions(
    actions => actions.clientAllocation,
  );

  const { filters, pagination, list } = useStoreState(state => state.clientAllocation);

  const currentFilters = useMemo(() => {
    if (isGlobalUser) {
      return {
        filters,
        pagination,
      };
    }
    return {
      filters,
      pagination,
    };
  }, [isGlobalUser, filters, pagination]);

  const getAllocations = useMemo(() => {
    if (isGlobalUser) {
      return (props: { clinicId: string; teamId: string; params?: IParams & IDateRange }) =>
        onGetGlobalUserClientAllocations({
          ...props,
          params: {
            ...props.params,
            allocationType: AllocationTypes.ALL,
          },
        });
    }
    return onGetClientAllocations;
  }, [isGlobalUser, onGetClientAllocations, onGetGlobalUserClientAllocations]);

  const currentList = useMemo(() => {
    if (isGlobalUser) {
      return list;
    }
    return list;
  }, [isGlobalUser, list]);

  const dateRange = useMemo(
    () =>
      currentFilters.filters?.dateRange[0] && currentFilters.filters?.dateRange[1]
        ? [
            getLocalDateNoFormat(currentFilters.filters.dateRange[0]),
            getLocalDateNoFormat(currentFilters.filters.dateRange[1]),
          ]
        : [],
    [currentFilters.filters?.dateRange],
  );

  return {
    getAllocations,
    list: currentList,
    currentFilters,
    dateRange,
  };
};

export default useGetAllocations;
